import React, { ComponentType, ElementType, forwardRef } from 'react';
import {
  Box,
  BoxProps,
  IconButtonProps,
  SwipeableDrawer,
  SwipeableDrawerProps,
  IconButton,
  Typography,
  TypographyProps,
  useTheme,
  SvgIconProps,
  PaperProps,
} from '@mui/material';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';
import { useElementScrollable } from '@/src/utils/useElementScrollable';
import { getFocusRingBoxShadow } from '@/src/components/focusring/focusring';

export interface BottomSheetProps extends Omit<SwipeableDrawerProps, 'onClose' | 'onOpen' | 'title'> {
  /**
   * Open state component flag
   */
  open: boolean;
  /**
   * Open state component function
   */
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Flag for displaying close button
   */
  closeButton?: boolean;
  /**
   * Props applied to closeButton IconButton
   */
  closeButtonProps?: Partial<IconButtonProps>;
  /**
   * Props applied to Icon in closeButton
   */
  closeButtonIconProps?: SvgIconProps;
  /**
   * Icon node to be used
   */
  CloseButtonIconComponent?: ComponentType<SvgIconProps>;
  /**
   * Flag for displaying bar that visualizes pulling function
   */
  puller?: boolean;
  /**
   * Props applied to wrapper of puller
   */
  pullerWrapperProps?: BoxProps<ElementType>;
  /**
   * Props applied to puller
   */
  pullerProps?: BoxProps<ElementType>;
  /**
   * Footer children slot
   */
  footerSlot?: React.ReactNode;
  /**
   * Text in header
   */
  title?: React.ReactNode;
  /**
   * Text props
   */
  titleProps?: TypographyProps<ElementType>;
  /**
   * Max height of whole component in percentages
   * @default 60
   */
  maxHeight?: number;
  /**
   * Wrapper of header
   */
  headerWrapper?: BoxProps<ElementType>;
  /**
   * Wrapper of title and close button
   */
  titleWrapper?: BoxProps<ElementType>;
  /**
   * Wrapper of content
   */
  contentWrapper?: BoxProps<ElementType>;
  /**
   * Wrapper of footer
   */
  footerWrapper?: BoxProps<ElementType>;
}

export const BottomSheet: React.FC<BottomSheetProps> = forwardRef(
  (
    {
      children,
      open,
      setOpen,
      slotProps,
      puller,
      pullerWrapperProps,
      pullerProps,
      closeButton,
      closeButtonProps,
      closeButtonIconProps,
      CloseButtonIconComponent,
      footerSlot,
      title,
      titleProps = {},
      headerWrapper,
      titleWrapper,
      contentWrapper,
      footerWrapper,
      maxHeight = 60,
      ...rest
    },
    ref,
  ) => {
    const { sx: headerSx, ...restHeader } = headerWrapper ?? {};
    const { sx: titleWrapperSx, ...restTitleWrapper } = titleWrapper ?? {};
    const { sx: titleSx, ...restTitle } = titleProps ?? {};
    const { sx: pullerWrapperSx, ...restPullerWrapper } = pullerWrapperProps ?? {};
    const { sx: pullerSx, ...restPuller } = pullerProps ?? {};
    const { sx: closeButtonSx, ...restCloseButton } = closeButtonProps ?? {};
    const { sx: contentSx, ...restContent } = contentWrapper ?? {};
    const { sx: footerSx, ...restFooter } = footerWrapper ?? {};
    const theme = useTheme();
    const BottomSheetCloseButtonIcon = CloseButtonIconComponent || CloseIcon;

    const { isScrollable, elementRef } = useElementScrollable('y');

    return (
      <SwipeableDrawer
        dir={theme.direction}
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        disableSwipeToOpen
        ModalProps={{
          keepMounted: true,
        }}
        data-testid="BottomSheet"
        slotProps={{
          ...slotProps,
          paper: {
            ...slotProps?.paper,
            sx: {
              boxShadow: 'none',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              maxHeight: `${maxHeight}%`,
              ...(slotProps?.paper as PaperProps)?.sx,
            },
          },
        }}
        ref={ref}
        {...rest}
      >
        {(title || closeButton || puller) && (
          <Box sx={{ px: 2, ...headerSx }} data-testid="BottomSheet-header" {...restHeader}>
            {puller && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  ...pullerWrapperSx,
                }}
                {...restPullerWrapper}
              >
                <Box
                  data-testid="BottomSheet-puller"
                  sx={{
                    width: '28px',
                    height: '4px',
                    backgroundColor: 'onSurface.os500',
                    mt: 1,
                    mb: 1.5,
                    borderRadius: '8px',
                    ...pullerSx,
                  }}
                  {...restPuller}
                />
              </Box>
            )}
            {(title || (closeButton && !puller)) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: title ? 'space-between' : 'flex-end',
                  alignItems: 'flex-start',
                  gap: 1,
                  pt: isScrollable && puller ? 1 : 0,
                  ...titleWrapperSx,
                }}
                {...restTitleWrapper}
              >
                {title && (
                  <Typography
                    data-testid="BottomSheet-title"
                    variant="h4"
                    component="h2"
                    sx={{
                      mt: puller ? 0 : 2,
                      mb: isScrollable ? 2 : 0,
                      ...titleSx,
                    }}
                    {...restTitle}
                  >
                    {title}
                  </Typography>
                )}
                {closeButton && !puller && (
                  <IconButton
                    data-testid="BottomSheet-closeButton"
                    size="medium"
                    onClick={() => setOpen(false)}
                    aria-label="Close bottom sheet"
                    sx={{
                      my: '4px',
                      mr: '-6px',
                      ...closeButtonSx,
                    }}
                    {...restCloseButton}
                  >
                    <BottomSheetCloseButtonIcon {...closeButtonIconProps} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        )}
        <Box
          data-testid="BottomSheet-content"
          ref={elementRef}
          sx={{
            overflow: 'auto',
            borderTop: isScrollable ? 1 : 0,
            borderBottom: isScrollable && footerSlot ? 1 : 0,
            borderColor: 'surface.secondary',
            p: 2,
            // can be focused since scrollable
            '&:focus-visible': {
              outline: 'none',
              boxShadow: getFocusRingBoxShadow(theme, true),
            },
            ...contentSx,
          }}
          {...restContent}
        >
          {children}
        </Box>
        {footerSlot && (
          <Box
            data-testid="BottomSheet-footer"
            sx={{
              px: 2,
              pt: 3,
              pb: 2,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'stretch',
              flexDirection: 'column',
              gap: '16px',
              ...footerSx,
            }}
            {...restFooter}
          >
            {footerSlot}
          </Box>
        )}
      </SwipeableDrawer>
    );
  },
);
